import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { WorkspaceMembershipSummary } from '@floorpoll/shared';
import { ApiError, apiRequest } from './api.js';
import {
  adoptTokens as storeTokens,
  clearSession,
  getRefreshToken,
  onSessionLost,
  onSessionStatus,
  renewSession,
  startSessionWatch,
  stopSessionWatch,
  type SessionStatus,
  type Tokens,
} from './session.js';

interface Me {
  user: {
    userId: string;
    email: string;
    name: string;
    /** Absent until this account accepts the current documents. */
    termsVersion?: string;
    termsAcceptedAt?: string;
  };
  workspaces: WorkspaceMembershipSummary[];
  platformAdmin: boolean;
}

interface AuthState {
  status: 'loading' | 'signed-in' | 'signed-out';
  user: Me['user'] | null;
  workspaces: WorkspaceMembershipSummary[];
  /** Membership of the cross-workspace `platform-admin` Cognito group. */
  platformAdmin: boolean;
  /** Whether the token lifecycle can currently reach the API. */
  sessionStatus: SessionStatus;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Adopts tokens obtained out of band, such as the Google redirect. */
  adoptTokens: (tokens: Tokens) => Promise<void>;
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [user, setUser] = useState<Me['user'] | null>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceMembershipSummary[]>([]);
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('active');

  const signOut = useCallback(() => {
    clearSession();
    setUser(null);
    setWorkspaces([]);
    setPlatformAdmin(false);
    setSessionStatus('active');
    setStatus('signed-out');
  }, []);

  const refreshMe = useCallback(async () => {
    const me = await apiRequest<Me>('/me');
    setUser(me.user);
    setWorkspaces(me.workspaces);
    setPlatformAdmin(me.platformAdmin === true);
  }, []);

  const adoptTokens = useCallback(
    async (tokens: Tokens) => {
      storeTokens(tokens);
      await refreshMe();
      setStatus('signed-in');
    },
    [refreshMe],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      const tokens = await apiRequest<Tokens>('/auth/signin', {
        method: 'POST',
        body: { email, password },
        anonymous: true,
      });
      await adoptTokens(tokens);
    },
    [adoptTokens],
  );

  // Restore a session on load, keep the console alive through an outage, and
  // sign out only when Cognito actually rejects the refresh token.
  useEffect(() => {
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | null = null;

    const completeSignIn = async () => {
      try {
        await refreshMe();
        if (!cancelled) setStatus('signed-in');
      } catch (error) {
        if (cancelled) return;
        // A 401 here does NOT mean the credential is dead. `apiRequest` has
        // already tried to renew and failed, and a refresh token Cognito
        // genuinely rejected would have fired `onSessionLost` on the way —
        // signing us out before this line runs. So a 401 arriving here means
        // the renewal could not reach Cognito and the id token is merely
        // stale. Signing out on it would delete a working 90-day credential,
        // which is the bug this whole change exists to fix.
        if (
          error instanceof ApiError &&
          error.status >= 400 &&
          error.status < 500 &&
          error.status !== 401
        ) {
          signOut();
          return;
        }
        // A definitive rejection already cleared the credential, so there is
        // nothing left to complete — retrying would poll `/me` forever behind
        // the sign-in page.
        if (!getRefreshToken()) return;
        // Overlapping failures overwrite this handle, so the cleanup below can
        // only clear the last one. A stray timer therefore checks for itself,
        // rather than waking up after unmount to fetch `/me` for nobody.
        retry = setTimeout(() => {
          if (!cancelled) void completeSignIn();
        }, 5_000);
      }
    };

    onSessionLost(() => signOut());
    onSessionStatus((next) => {
      if (cancelled) return;
      setSessionStatus(next);
      // Only ever a transition, so this means the network just came back:
      // finish a sign-in that started offline, and re-read a workspace list
      // that may have changed while we were away.
      if (next === 'active') void completeSignIn();
    });
    startSessionWatch();

    void (async () => {
      const outcome = await renewSession();
      if (cancelled) return;

      // 'unavailable' keeps the refresh token and retries in the background,
      // so the gate stays on 'loading' and the status listener above finishes
      // the job. Signing out here would throw away a working session.
      if (outcome === 'expired') setStatus('signed-out');
      else if (outcome === 'renewed') await completeSignIn();
    })();

    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
      onSessionLost(null);
      onSessionStatus(null);
      stopSessionWatch();
    };
  }, [refreshMe, signOut]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      workspaces,
      platformAdmin,
      sessionStatus,
      signIn,
      signOut,
      adoptTokens,
      refreshMe,
    }),
    [
      status,
      user,
      workspaces,
      platformAdmin,
      sessionStatus,
      signIn,
      signOut,
      adoptTokens,
      refreshMe,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an AuthProvider');
  return context;
}
